import { ReactElement, useCallback, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { generatePath, useLocation, useNavigate } from 'react-router-dom';

import EquipmentEmptyImage from 'assets/images/equipment.png';
import { AddAndExtendActions } from 'components';
import { useAuth } from 'hooks';
import { Links } from 'settings';
import { EquipmentPayload } from 'types/equipments';

import { SeeAllLink } from '../PrivateDocuments/styled';
import {
  ContainerW,
  EmptyStateTitle,
  ListItem,
  ListContainer,
  Title,
  EmptyStateContainer,
  EmptyStateImage,
  NavLinkW,
  Header,
  EquipmentListWrapper,
} from './styled';

interface EquipmentListProps {
  list: EquipmentPayload[];
  employeeId: string;
  equipmentExist?: boolean;
}

export function EquipmentList({
  list,
  employeeId,
  equipmentExist = false,
}: EquipmentListProps): ReactElement {
  const { isManager, user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const linkState = useMemo(() => ({ from: pathname }), [pathname]);

  const isListLength = list.length > 0;

  const createEquipmentLink = generatePath(Links.profile.equipmentList.createEquipment, {
    employeeId,
  });
  const viewEquipmentsLink = generatePath(Links.profile.equipmentList.equipment.index, {
    employeeId,
  });

  const handleItemClick = useCallback(
    ({ id, user_id, author_id, is_editable }: EquipmentPayload) => {
      const isCurrentAuthor = user?.id === author_id;
      const isCurrentAssignee = user?.id === user_id;
      const isEditableByManager =
        isManager &&
        ((isCurrentAuthor && isCurrentAssignee && is_editable) ||
          (isCurrentAuthor && !isCurrentAssignee) ||
          (!isCurrentAuthor && !isCurrentAssignee));

      const equipmentLink = generatePath(Links.profile.equipmentList.equipment.equipmentId.index, {
        employeeId: user_id,
        equipmentId: id,
      });

      (isEditableByManager || is_editable) && navigate(equipmentLink, { state: linkState });
    },
    [isManager, linkState, navigate, user?.id],
  );

  return (
    <ContainerW isListLength={isListLength}>
      <Header>
        <Title>{t('profile.equipments.preview.title')}</Title>
        <AddAndExtendActions
          addIconLink={createEquipmentLink}
          addLinkState={linkState}
          equipments
        />
      </Header>
      {equipmentExist ? (
        <EquipmentListWrapper>
          <ListContainer>
            {list.map((listItem) => (
              <ListItem key={listItem.id} onClick={() => handleItemClick(listItem)}>
                {listItem.name}
              </ListItem>
            ))}
            <SeeAllLink onClick={() => navigate(viewEquipmentsLink)}>
              {t('profile.equipments.preview.seeAll')}
            </SeeAllLink>
          </ListContainer>
        </EquipmentListWrapper>
      ) : (
        <EmptyStateContainer>
          <EmptyStateImage src={EquipmentEmptyImage} alt="equipment empty state" />
          <EmptyStateTitle>
            <Trans i18nKey="profile.equipments.preview.emptyState">
              Nothing is here yet.
              <NavLinkW to={createEquipmentLink} state={linkState}>
                Add Equipments
              </NavLinkW>
              !
            </Trans>
          </EmptyStateTitle>
        </EmptyStateContainer>
      )}
    </ContainerW>
  );
}
